import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, AfterViewInit } from '@angular/core';
import {ChatMessage} from '../../models/chat-message';
import {ChatMessageChunk} from '../../models/chat-message-chunk';
import {FallbackImageService} from '../../services/fallback-image.service';
import {WebSocketService} from '../../services/websocket.service';
import {AuthService} from '../../services/auth/auth.service';
import { ChatMessageProcessor } from '../../services/chat/chat-message-processor.service';
import {Subscription} from 'rxjs';
import {CommonModule, NgOptimizedImage} from '@angular/common';
import {marked} from 'marked';
import DOMPurify from 'dompurify';
import {ProgressUpdate} from "../../models/progress-update";
import {LoggingService} from '../../services/logging/logging.service';
import { PerformanceMetricsService } from '../../services/performance-metrics.service'; // Add this
import { ErrorTrackingService } from '../../services/error-tracking.service'; // Add this
import { ConsoleLogsCaptureService } from '../../services/console-logs-capture.service';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { environment } from '../../../environments/environment';

/**
 * Extended interface for internal use that adds timing information
 */
interface ProgressUpdateWithTimingInfo extends ProgressUpdate {
  numericTimestamp?: number;
  elapsedMs?: number;
}

/**
 * Interface for chunk tracing information
 */
interface ChunkTraceInfo {
  sequenceNumber: number;
  content: string;
  receivedTime: number;
  traceId?: string;
  isDuplicate: boolean;
  chunkIndex: number;
}

/**
 * MessageComponent displays a single chat message in the conversation.
 *
 * It now includes a collapsible progress log that can be expanded to show
 * operation details and progress history.
 */
@Component({
  selector: 'app-message',
  templateUrl: './message.component.html',
  styleUrls: ['./message.component.scss'],
  standalone: true,
  imports: [CommonModule, NgOptimizedImage],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('messageAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(10px)' }))
      ])
    ])
  ]
})
export class MessageComponent implements OnInit, OnDestroy, AfterViewInit {
  /**
   * The chat message to display.
   * Contains a BehaviorSubject<string> for content that emits as chunks arrive.
   */
  @Input() chatMessage!: ChatMessage;

  /**
   * Flag indicating if this is the last bot message in the conversation.
   * Used to determine if response time metrics should be displayed.
   */
  @Input() isLastBotMessage: boolean = false;

  /**
   * Demo mode to show portal even when not loading
   */
  @Input() demoPortal: boolean = false;

  /**
   * Flag indicating if this is the first bot message (for image priority)
   */
  @Input() isFirstBotMessage: boolean = false;

  /**
   * The text content of the message, updated as chunks arrive.
   */
  typewriterText: string | null = null;

  /**
   * Flag indicating if the message is in loading state
   */
  isLoading: boolean = false;

  /**
   * The URL for the avatar image with fallback handling.
   */
  safeImageUrl: string = '';

  /**
   * Current phase data for the primary displayed phase
   */
  currentPhase: ProgressUpdateWithTimingInfo | null = null;

  /**
   * Store progress history to allow expanding the thought process
   */
  progressHistory: ProgressUpdateWithTimingInfo[] = [];

  /**
   * Flag to control expanded state of the progress container
   */
  isProgressExpanded: boolean = false;

  /**
   * Flag to show progress trace even after content is loaded
   */
  showProgressTrace: boolean = true;

  /**
   * Total seconds the operation spent processing
   */
  processingTimeSeconds: number = 0;

  /**
   * All active operations to show in expanded view
   */
  activeOperations: Map<string, ProgressUpdate> = new Map();

  /**
   * Progress messages currently visible in the portal
   */
  activeProgressMessages: Array<{ id: string; message: string; timestamp: number }> = [];

  /** ensure we only add the synthetic step once */
  private syntheticStepAdded = false;

  private progressSubscription: Subscription | null = null;
  private operationsSubscription: Subscription | null = null;

  /**
   * Calculated response time for display.
   */
  responseTime: number | null = null;

  /**
   * Timer reference for periodic response time checks
   */
  private responseTimeCheckInterval: any = null;

  /**
   * Timer to update processingTimeSeconds when the message is still loading
   */
  private processingTimeTimer: any = null;

  /**
   * Flag to indicate we're showing the initial immediate loading indicator
   * Added to fix mobile delay issue
   */
  showInitialLoadingIndicator: boolean = false;

  /**
   * Modern chat UX (2025): No debouncing for streaming text
   * Removed contentBuffer and bufferTimeout - chunks displayed immediately
   */

  /**
   * Flag to determine if we're in development mode for trace visibility
   */
  isDevelopmentMode: boolean = false;

  /**
   * Base URL for Jaeger UI
   */
  jaegerBaseUrl: string = 'http://10.0.0.6:16686';

  constructor(
    private fallbackImageService: FallbackImageService,
    private cdr: ChangeDetectorRef,
    private webSocketService: WebSocketService,
    private logger: LoggingService,
    private performanceMetrics: PerformanceMetricsService,
    private errorTracking: ErrorTrackingService,
    private authService: AuthService,
    private chatMessageProcessorService: ChatMessageProcessor,
    private consoleLogsCapture: ConsoleLogsCaptureService
  ) {}

  ngOnInit(): void {
    // Detect development mode for trace visibility
    this.isDevelopmentMode = !environment.production && (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '10.0.0.6' ||
      window.location.hostname.startsWith('192.168.')
    );

    // Track component initialization start for bot messages
    if (this.chatMessage.bot) {
      this.performanceMetrics.markStart(`message_init_${this.getMessageId()}`);
    }

    // Get initial text value
    this.typewriterText = this.chatMessage.content.getValue();

    // Demo mode: populate with sample messages
    if (this.demoPortal && this.chatMessage.bot && this.isLastBotMessage) {
      this.populateDemoMessages();
    }

    // Set initial loading state - a bot message with no content is in loading state
    this.isLoading = this.chatMessage.bot && (!this.typewriterText || this.typewriterText.trim() === '');

    // IMPROVEMENT: Immediately show loading indicator for bot messages with no content
    this.showInitialLoadingIndicator = this.isLoading;

    // Calculate response time if available
    this.calculateResponseTime();

    // Start tracking processing time if this is a loading message
    if (this.isLoading && this.chatMessage.generationStartTime) {
      this.startProcessingTimeTracking();
    }

    this.logger.info('MessageComponent', 'Initial state:', {
      isBot: this.chatMessage.bot,
      isLastBotMessage: this.isLastBotMessage,
      isLoading: this.isLoading,
      responseTime: this.responseTime,
      messageId: this.chatMessage.messageId,
      operationType: this.chatMessage.operationType,
      contentLength: this.typewriterText?.length || 0,
      shouldShowProgressPortal: this.shouldShowProgressPortal(),
      hasActiveProgressUpdates: this.hasActiveProgressUpdates(),
      demoPortal: this.demoPortal
    });

    // Subscribe to text changes from the BehaviorSubject
    this.logger.info('MessageComponent', `Setting up content subscription for message ${this.getMessageId()}`);
    this.chatMessage.content.subscribe(value => {
      // Process content immediately (log inside processContentChunk)
      this.processContentChunk(value);
    }, error => {
      this.errorTracking.trackError('message_content_subscription', error, {
        messageId: this.getMessageId(),
        isBot: this.chatMessage.bot
      });
    });

    // Get safe image URL with fallback handling
    // FIN-0104: Pass isBot for distinct avatar colors (bot: mint, user: gray)
    const userInfo = this.authService.getUserAuthInfo();
    const userName = this.chatMessage.bot ? undefined : userInfo?.fullName || userInfo?.givenName;
    this.safeImageUrl = this.fallbackImageService.getSafeImageUrl(this.chatMessage.pictureUrl, userName, this.chatMessage.bot);

    // For bot messages, especially the last bot message, set up a timer to periodically
    // check for response time updates.
    if (this.chatMessage.bot && this.isLastBotMessage && !this.hasResponseTime()) {
      this.responseTimeCheckInterval = setInterval(() => {
        // Stop checking if we already have a response time or if the message is no longer the last bot message
        if (this.hasResponseTime() || !this.isLastBotMessage) {
          this.clearResponseTimeInterval();
          return;
        }

        const previousResponseTime = this.responseTime;
        this.calculateResponseTime();

        // Only trigger change detection if the response time actually changed
        if (previousResponseTime !== this.responseTime) {
          this.cdr.markForCheck();
        }
      }, 500); // Check every 500ms
    }

    // Only subscribe to operations if this is the last bot message
    if (this.chatMessage.bot && this.isLastBotMessage) {
      this.subscribeToAllOperations();
    }

    // If this is a bot message, mark the end of initialization
    if (this.chatMessage.bot) {
      this.performanceMetrics.markEnd(
        `message_init_${this.getMessageId()}`,
        'bot_message_component_initialized',
        {
          isLoading: this.isLoading,
          isLastBotMessage: this.isLastBotMessage,
          hasContent: !!this.typewriterText && this.typewriterText.trim().length > 0
        }
      );
    }
  }

  ngAfterViewInit(): void {
    if (this.chatMessage.bot) {
      // Track when message is visible in the DOM
      this.performanceMetrics.trackMilestone('bot_message_rendered', {
        messageId: this.getMessageId(),
        isLoading: this.isLoading,
        isLastBotMessage: this.isLastBotMessage
      });
    }
  }

    // Add a helper method to get a consistent message identifier
  private getMessageId(): string {
    return this.chatMessage.timestamp || `msg-${Date.now()}`;
  }

  ngOnDestroy(): void {
    this.clearResponseTimeInterval();
    this.stopProcessingTimeTracking();

    // No buffer cleanup needed - removed debouncing for modern streaming UX

    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
      this.progressSubscription = null;
    }

    if (this.operationsSubscription) {
      this.operationsSubscription.unsubscribe();
      this.operationsSubscription = null;
    }
  }

  /**
   * Start a timer to update the processing time every second while loading
   */
  private startProcessingTimeTracking(): void {
    // Clean up any existing timer first
    this.stopProcessingTimeTracking();

    // Set initial value to at least 1 second to avoid showing "0 seconds"
    if (this.chatMessage.generationStartTime) {
      const initialMs = Date.now() - this.chatMessage.generationStartTime;
      // Start with at least 1 to avoid "0 seconds" display
      this.processingTimeSeconds = Math.max(1, Math.round(initialMs / 1000));
      this.cdr.markForCheck();
    }

    // Set up timer for regular updates
    this.processingTimeTimer = setInterval(() => {
      this.updateProcessingTime();
    }, 1000);
  }

  /**
   * Update processing time based on start time and current time
   */
  private updateProcessingTime(): void {
    if (this.chatMessage.generationStartTime && this.isLoading) {
      const now = Date.now();
      const elapsedMs = now - this.chatMessage.generationStartTime;
      // Always show at least 1 second to avoid "0 seconds" display
      this.processingTimeSeconds = Math.max(1, Math.round(elapsedMs / 1000));
      this.cdr.markForCheck();
    }
  }

  /**
   * Stop the processing time tracking timer
   */
  private stopProcessingTimeTracking(): void {
    if (this.processingTimeTimer !== null) {
      clearInterval(this.processingTimeTimer);
      this.processingTimeTimer = null;
    }
  }

  /**
   * Subscribe to progress updates for all active operations (type-agnostic)
   */
  private subscribeToAllOperations(): void {
    this.logger.info('MessageComponent','Subscribing to all operation updates');
    this.operationsSubscription = this.webSocketService.operations$.subscribe(operations => {
      // Clear and rebuild the active operations map
      this.activeOperations.clear();
      operations.forEach(opSubject => {
        const update = opSubject.getValue();
        if (update) {
          this.activeOperations.set(update.operationId, update);
        }
      });
      // Only process if there are relevant operations
      if (this.activeOperations.size > 0 && this.isLastBotMessage) {
        const highest = Array.from(this.activeOperations.values()).reduce((a, b) => a.progress > b.progress ? a : b);
        this.processProgressUpdate(highest);
      }
    });
  }

  /**
   * Process an incoming progress update
   * Handles progress updates generically without hardcoded phases
   */
  private processProgressUpdate(update: ProgressUpdate): void {
    if (!update) return;

    // Once we get a real progress update, we can hide the initial loading indicator
    this.showInitialLoadingIndicator = false;

    // Add all updates to the history so we don't lose any steps
    const extendedUpdate: ProgressUpdateWithTimingInfo = {
      ...update,
      numericTimestamp: Date.now()
    };
    this.addToProgressHistory(extendedUpdate);

    // Determine if this is a significant update for progress tracking
    // This only affects which update is shown as the current phase
    const isMajorUpdate = this.isSignificantUpdate(update);

    // Major updates also become the current visible phase
    if (isMajorUpdate) {
      this.updateCurrentPhase(update);
    }
  }

  /**
   * Determine if a progress update is significant enough to show as a current phase
   * Uses heuristics instead of hardcoded phase names
   */
  private isSignificantUpdate(update: ProgressUpdate): boolean {
    // Skip completion messages as current phases
    if (update.message.toLowerCase().includes('completed') && update.message.toLowerCase() !== 'substep completed') {
      return false;
    }

    // Every message is significant for history purposes
    // This ensures we maintain a complete history of updates
    return true;
  }

  /**
   * Update the current phase display
   */
  private updateCurrentPhase(update: ProgressUpdate): void {
    // Skip completion messages as current phases
    if (update.message.toLowerCase().includes('completed')) {
      // Just add to history but don't update current phase
      const extendedUpdate: ProgressUpdateWithTimingInfo = {
        ...update,
        numericTimestamp: Date.now()
      };
      this.addToProgressHistory(extendedUpdate);
      return;
    }

    // Create an extended update with timing info
    const updatedProgress: ProgressUpdateWithTimingInfo = {
      ...update,
      numericTimestamp: Date.now()
    };

    // Calculate elapsed time if we have a previous phase
    if (this.currentPhase && this.currentPhase.numericTimestamp && updatedProgress.numericTimestamp) {
      updatedProgress.elapsedMs = updatedProgress.numericTimestamp - this.currentPhase.numericTimestamp;
    }

    // Update the current phase
    this.currentPhase = updatedProgress;

    // Also add to history for the expanded view
    this.addToProgressHistory(updatedProgress);

    this.cdr.markForCheck();
  }

  /**
   * Add timestamp to each progress update when stored
   * This is used to calculate elapsed time between steps
   */
  private addToProgressHistory(update: ProgressUpdateWithTimingInfo): void {
    // With the new approach, we want to keep more updates
    // Still skip consecutive duplicate messages to avoid clutter
    const lastUpdate = this.progressHistory.length > 0 ?
      this.progressHistory[this.progressHistory.length - 1] : null;

    if (lastUpdate && lastUpdate.message === update.message && lastUpdate.type === update.type) {
      // Just update the progress of the last entry
      lastUpdate.progress = update.progress;
      return;
    }

    // Add new update to history
    this.progressHistory.push(update);

    // Add to active messages for portal display (but not "Operation completed" type messages)
    if (update.message && !update.message.toLowerCase().includes('complet') && !update.message.toLowerCase().includes('done')) {
      const messageId = `${Date.now()}-${Math.random()}`;
      this.activeProgressMessages.push({
        id: messageId,
        message: update.message,
        timestamp: Date.now()
      });

      // Keep only the last 3 messages visible
      if (this.activeProgressMessages.length > 3) {
        this.activeProgressMessages.shift();
      }

      // Auto-remove messages after 5 seconds for better visibility
      setTimeout(() => {
        const index = this.activeProgressMessages.findIndex(msg => msg.id === messageId);
        if (index >= 0) {
          this.activeProgressMessages.splice(index, 1);
          this.cdr.markForCheck();
        }
      }, 5000);
    }

    // Allow a larger history size since we're showing more steps
    if (this.progressHistory.length > 50) {
      this.progressHistory.shift();
    }

    // Hide loading UI if trace is now visible
    if (this.progressHistory.length > 1 && this.isLoading) {
      this.isLoading = false;
      this.cdr.markForCheck();
    }

    // keep the "Thought for . seconds" header in sync
    this.processingTimeSeconds = this.getTraceDurationSeconds();
    this.cdr.markForCheck();
  }

  getLoadingMessage(): string {
    // Special cases based on operation type
    if (this.chatMessage.operationType === 'CONNECTING') {
      return 'Connecting to server...';
    } else if (this.chatMessage.operationType === 'ERROR') {
      return 'Connection error. Please try again later.';
    }

    // Use the current phase message if available, otherwise default
    return this.currentPhase?.message || '';
  }

  /**
   * Total duration covered by the trace
   */
  private getTraceDurationSeconds(): number {
    if (this.progressHistory.length < 2) return this.processingTimeSeconds;

    const first = this.progressHistory[0].numericTimestamp;
    const last = this.progressHistory[this.progressHistory.length - 1].numericTimestamp;

    if (first === undefined || last === undefined) {
      return this.processingTimeSeconds;
    }

    return Math.max(1, Math.round((last - first) / 1000));
  }

  /**
   * Calculate the elapsed time between this step and the previous one
   */
  getElapsedTimeBetweenSteps(thought: ProgressUpdateWithTimingInfo, index: number): string {
    if (!thought.numericTimestamp) {
      return '';
    }

    // For the first step, calculate from operation start time
    if (index === 0 && this.chatMessage.generationStartTime) {
      const elapsed = thought.numericTimestamp - this.chatMessage.generationStartTime;
      return this.formatElapsedTime(elapsed);
    }

    // For later steps, calculate from previous step
    const previousStep = this.progressHistory[index-1];
    if (index > 0 && previousStep && previousStep.numericTimestamp !== undefined) {
      const elapsed = thought.numericTimestamp - previousStep.numericTimestamp;
      return this.formatElapsedTime(elapsed);
    }

    return '';
  }

  /**
   * Format elapsed time in ms to human-readable format
   */
  private formatElapsedTime(elapsedMs: number): string {
    if (elapsedMs < 1000) {
      return `${elapsedMs}ms`;
    } else {
      const seconds = (elapsedMs / 1000).toFixed(1);
      return `${seconds}s`;
    }
  }

  /**
   * Toggle expanded state of the progress container
   */
  toggleProgressExpanded(): void {
    this.isProgressExpanded = !this.isProgressExpanded;
    this.cdr.markForCheck();
  }

  /**
   * TrackBy function for progress messages
   */
  trackByMessageId(index: number, item: { id: string; message: string; timestamp: number }): string {
    return item.id;
  }

  /**
   * Hide the progress trace completely
   */
  hideProgressTrace(): void {
    this.showProgressTrace = false;
    this.isProgressExpanded = false;
    this.cdr.markForCheck();
  }

  /**
   * Get active operations as an array for rendering
   */
  getActiveOperations(): ProgressUpdate[] {
    return Array.from(this.activeOperations.values());
  }

  /**
   * Check if there are any active operations
   */
  hasActiveOperations(): boolean {
    return this.activeOperations.size > 0;
  }

  /**
   * Check if we have progress history to show
   */
  hasProgressHistory(): boolean {
    return this.progressHistory.length > 0;
  }

  /**
   * Check if we have an "Operation completed" message
   * Uses string matching instead of exact phrase
   */
  hasOperationCompletedMessage(): boolean {
    return this.progressHistory.some(update =>
      update.message.toLowerCase().includes('completed') ||
      update.message === 'Drafting reply...'  // Also check for our synthetic completion step
    );
  }

  /**
   * Determine if this operation has active progress updates that warrant showing the progress portal
   */
  private hasActiveProgressUpdates(): boolean {
    return this.activeProgressMessages.length > 0 || 
           this.progressHistory.length > 1 ||
           this.activeOperations.size > 0;
  }

  /**
   * Determine if the progress portal should be shown
   * Only show when we have actual progress updates from the backend or in demo mode
   */
  shouldShowProgressPortal(): boolean {
    // Always show in demo mode
    if (this.demoPortal) {
      return true;
    }
    
    // Show portal only if we have actual progress updates from the backend
    // This ensures the portal is driven by backend progress, not time
    return this.hasActiveProgressUpdates();
  }

  /**
   * Clear the response time check interval
   */
  private clearResponseTimeInterval(): void {
    if (this.responseTimeCheckInterval) {
      clearInterval(this.responseTimeCheckInterval);
      this.responseTimeCheckInterval = null;
    }
  }

  /**
   * Determine if we have a valid response time to display.
   * Only shows response time for the last bot message.
   */
  hasResponseTime(): boolean {
    return this.chatMessage.bot &&
      this.isLastBotMessage &&
      !!this.responseTime &&
      this.responseTime > 0;
  }

  /**
   * Calculate response time from the message properties.
   */
  private calculateResponseTime(): void {
    // First check if we have an explicit responseTimeMs property
    if (this.chatMessage.responseTimeMs && this.chatMessage.responseTimeMs > 0) {
      this.responseTime = this.chatMessage.responseTimeMs;
    }
    // If not, calculate it from start/end times if available
    else if (this.chatMessage.generationStartTime && this.chatMessage.generationEndTime) {
      const calculatedTime = this.chatMessage.generationEndTime - this.chatMessage.generationStartTime;
      if (calculatedTime > 0) {
        this.responseTime = calculatedTime;
      }
    }
  }

  /**
   * Convert markdown text to HTML for rendering.
   */
  formatStringToHtml(content: string): string {
    if (!content) {
      return '';
    }

    try {
      // Convert markdown to HTML using marked library
      const rawHtml = marked.parse(content);

      // Sanitize the HTML to prevent XSS attacks
      const sanitizedHtml = DOMPurify.sanitize(rawHtml as string);

      return sanitizedHtml;
    } catch (error) {
      console.error('Error formatting markdown:', error);
      return content; // Return original content if parsing fails
    }
  }

  /**
   * Format generation time in a human-readable way.
   */
  formatGenerationTime(timeMs: number): string {
    if (timeMs < 1000) {
      return `${Math.round(timeMs)}ms`;
    } else {
      const seconds = (timeMs / 1000).toFixed(1);
      return `${seconds}s`;
    }
  }

  /**
   * Handle image loading errors by switching to default image.
   */
  onImageError(): void {
    // Track the error
    this.errorTracking.trackError('message_image_load', 'Failed to load message avatar', {
      imageUrl: this.chatMessage.pictureUrl,
      isBot: this.chatMessage.bot,
      messageId: this.getMessageId()
    });

    // Mark the URL as failed for future reference
    this.fallbackImageService.markUrlAsFailed(this.chatMessage.pictureUrl);

    // FIN-0104: Switch to initials avatar with correct isBot flag for distinct colors
    const userInfo = this.authService.getUserAuthInfo();
    const userName = this.chatMessage.bot ? undefined : userInfo?.fullName || userInfo?.givenName;
    this.safeImageUrl = this.fallbackImageService.generateInitialsAvatar(userName, this.chatMessage.bot);
  }

  /**
   * Check if the given URL is a data URL (Base64 encoded).
   * Angular's NgOptimizedImage doesn't support data URLs, so we need to use regular src for those.
   */
  isDataUrl(url: string): boolean {
    return url?.startsWith('data:') || false;
  }

  /**
   * Process content chunks with immediate streaming display
   * Modern chat UX (2025): Show text as each chunk arrives (typewriter effect)
   * No debouncing - matches ChatGPT, Claude, and other modern AI chat interfaces
   */
  private processContentChunk(value: string): void {
    const previousTextLength = this.typewriterText?.length || 0;
    const wasLoading = this.isLoading;

    // Immediate update - no debouncing buffer
    this.typewriterText = value;
    this.updateLoadingState();

    // Single compact log for content update
    if (value && value.length > 0) {
      const msgId = this.getMessageId().slice(-12); // Last 12 chars for brevity
      this.logger.info('MessageComponent', `${msgId} | ${previousTextLength}→${value.length} chars | loading: ${wasLoading}→${this.isLoading}`);
    }

    // Trigger change detection immediately for OnPush strategy
    this.cdr.markForCheck();

    // Recalculate response time on content update
    this.calculateResponseTime();
  }

  /**
   * Update loading state when content changes
   * Separated from content processing for better control
   */
  private updateLoadingState(): void {
    if (this.typewriterText && this.typewriterText.trim().length > 0) {
      const wasLoading = this.isLoading;
      this.isLoading = false;
      this.showInitialLoadingIndicator = false;

      // Track when a loading message becomes content-filled (for bot messages only)
      if (wasLoading && this.chatMessage.bot) {
        this.performanceMetrics.trackMilestone('bot_message_content_loaded', {
          messageId: this.getMessageId(),
          contentLength: this.typewriterText.length,
          responseTimeMs: this.responseTime
        });
      }

      // Add synthetic "Drafting reply..." step when the content starts streaming
      // Only for substantial content to avoid showing for fragments
      if (this.chatMessage.bot && !this.syntheticStepAdded && this.typewriterText.length > 10) {
        this.updateCurrentPhase({
          operationId: 'generation',
          type: this.chatMessage.operationType ?? 'LLM',
          title: 'LLM',
          message: 'Drafting reply...',
          progress: 100,
          timestamp: new Date().toISOString()
        });
        this.syntheticStepAdded = true;
      }

      // Clear currentPhase when message finishes so we don't show it in minimized view
      if (this.currentPhase) {
        this.currentPhase = null;
      }

      // Calculate processing time when message completes
      if (this.chatMessage.generationStartTime && this.chatMessage.generationEndTime) {
        // Stop the processing time timer if it's running
        this.stopProcessingTimeTracking();
      }
    }
  }

  /**
   * Check if this is an initial message (no previous content)
   */
  private isInitialMessage(): boolean {
    return !this.typewriterText || this.typewriterText.trim() === '';
  }

  /**
   * Generate Jaeger trace URL for the given trace ID
   */
  getJaegerTraceUrl(traceId: string): string {
    return `${this.jaegerBaseUrl}/trace/${traceId}`;
  }

  /**
   * Copy trace ID to clipboard
   */
  async copyTraceId(traceId: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(traceId);
      this.logger.info('MessageComponent', 'Trace ID copied to clipboard', { traceId });
      // You could add a toast notification here if available
    } catch (error) {
      this.errorTracking.trackError('trace_id_copy', error, { traceId });
      // Fallback for older browsers
      this.fallbackCopyToClipboard(traceId);
    }
  }

  /**
   * Fallback method for copying to clipboard
   */
  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      this.logger.info('MessageComponent', 'Trace ID copied using fallback method', { text });
    } catch (error) {
      this.errorTracking.trackError('trace_id_copy_fallback', error, { text });
    } finally {
      document.body.removeChild(textArea);
    }
  }

  /**
   * Check if message has trace context for linking
   */
  hasTraceContext(): boolean {
    return !!(this.chatMessage.tracingContext?.traceId);
  }

  /**
   * Check if message has actual content (not empty or loading)
   */
  hasActualContent(): boolean {
    const currentContent = this.chatMessage.content.value;
    return !!(currentContent && currentContent.trim().length > 0);
  }

  /**
   * Check if trace UI is enabled
   */
  isTraceUIVisible(): boolean {
    return this.consoleLogsCapture.isTraceUIEnabled();
  }

  // Chunk debugging properties
  showChunkTracing = false;
  private chunkTraceInfo: ChunkTraceInfo[] = [];

  /**
   * Toggle chunk-level tracing display
   */
  toggleChunkTracing(): void {
    this.showChunkTracing = !this.showChunkTracing;
    if (this.showChunkTracing) {
      this.buildChunkTraceInfo();
    }
  }

  /**
   * Get total number of chunks received
   */
  getChunkCount(): number {
    return this.chunkTraceInfo.length;
  }

  /**
   * Get number of unique sequence numbers
   */
  getUniqueSequenceCount(): number {
    const uniqueSequences = new Set(this.chunkTraceInfo.map(chunk => chunk.sequenceNumber));
    return uniqueSequences.size;
  }

  /**
   * Get number of duplicate chunks detected
   */
  getDuplicateCount(): number {
    return this.chunkTraceInfo.filter(chunk => chunk.isDuplicate).length;
  }

  /**
   * Get chunk trace information for debugging
   */
  getChunkTraceInfo(): ChunkTraceInfo[] {
    return this.chunkTraceInfo;
  }

  /**
   * Track chunks by ID for ngFor performance
   */
  trackChunkById(index: number, chunk: ChunkTraceInfo): string {
    return `${chunk.sequenceNumber}-${chunk.receivedTime}`;
  }

  /**
   * Build chunk trace information from message processor data
   */
  private buildChunkTraceInfo(): void {
    // Get chunk tracking data from the message processor service
    if (!this.chatMessage.messageId) {
      this.chunkTraceInfo = [];
      return;
    }

    const messageTracker = this.chatMessageProcessorService.getMessageTracker(this.chatMessage.messageId);

    if (!messageTracker) {
      this.chunkTraceInfo = [];
      return;
    }

    // Process chunks and detect duplicates
    const sequenceMap = new Map<number, number>(); // sequence -> count

    this.chunkTraceInfo = messageTracker.chunks.map((chunk: ChatMessageChunk) => {
      const count = sequenceMap.get(chunk.sequenceNumber) || 0;
      sequenceMap.set(chunk.sequenceNumber, count + 1);

      // Parse timestamp to get received time
      const receivedTime = chunk.timestamp ? new Date(chunk.timestamp).getTime() : Date.now();

      return {
        sequenceNumber: chunk.sequenceNumber,
        content: chunk.content?.substring(0, 50) || '',
        receivedTime: receivedTime,
        traceId: chunk.tracingContext?.traceId,
        isDuplicate: count > 0, // This chunk is a duplicate if we've seen this sequence before
        chunkIndex: messageTracker.chunks.indexOf(chunk)
      };
    });

    // Sort by sequence number for better visualization
    this.chunkTraceInfo.sort((a, b) => {
      if (a.sequenceNumber === b.sequenceNumber) {
        return a.receivedTime - b.receivedTime; // Same sequence: sort by time
      }
      return a.sequenceNumber - b.sequenceNumber; // Different sequence: sort by sequence
    });
  }


  /**
   * Format time difference for display
   */
  formatTimeDifference(baseTime: number, currentTime: number): string {
    const diff = currentTime - baseTime;
    if (diff < 1000) {
      return `+${diff}ms`;
    } else {
      return `+${(diff / 1000).toFixed(1)}s`;
    }
  }

  /**
   * Populate demo messages for testing the portal
   */
  private populateDemoMessages(): void {
    const demoMessages = [
      'Checking your addresses',
      'Looking up location',
      'Finding your route',
      'Analyzing traffic patterns',
      'Calculating travel time'
    ];

    let delay = 0;
    demoMessages.forEach((message, index) => {
      setTimeout(() => {
        const messageId = `demo-${Date.now()}-${index}`;
        this.activeProgressMessages.push({
          id: messageId,
          message: message,
          timestamp: Date.now()
        });

        // Keep only the last 3 messages
        if (this.activeProgressMessages.length > 3) {
          this.activeProgressMessages.shift();
        }

        this.cdr.markForCheck();

        // Auto-remove after 5 seconds
        setTimeout(() => {
          const idx = this.activeProgressMessages.findIndex(msg => msg.id === messageId);
          if (idx >= 0) {
            this.activeProgressMessages.splice(idx, 1);
            this.cdr.markForCheck();
          }
        }, 5000);
      }, delay);
      delay += 1000; // Stagger messages by 1 second
    });
  }
}
